import { generatePath } from "react-router";
import { graphql } from "relay-runtime";
import { optional, pipe, string, toUpperCase, guard, safeParse } from "valibot";
import type { Semver, UsernameOrID } from "@/common/relayCustomScalar";
import { formatSlug } from "./format";
import { assertNever, invariant } from "./helpers";
import { intoPath } from "./url";

import type { ForumOwnerKind } from "./__generated__/routingFragment.graphql";
import type { ProjectVersionFileKind } from "./__generated__/routingFragment2.graphql";
import { OpenGraphParams } from "@/common/openGraph";

graphql`
  fragment routingFragment on ForumOwner {
    forumOwnerKind
  }
`;

graphql`
  fragment routingFragment2 on ProjectVersionFile {
    kind
  }
`;

export type PathSegment = string;
export type Path = PathSegment[];

export function formatPath(...args: Path): string {
  return args.join("/");
}

export function formatAbsolutePath(...args: Path): string {
  return "/" + formatPath(...args);
}

export interface ForumOwnerShape {
  slug: string;
  forumOwnerKind: ForumOwnerKind;
  parent?: PathSegment;
}

export interface ForumShape {
  slug: string;
  owner: ForumOwnerShape;
}

export interface TopicShape {
  id: ID;
  title: string;
  forum: ForumShape;
}

export interface BlogArticleShape {
  slugLower: string;
}

export function getForumPath({ slug, owner }: ForumShape, ...rest: Path) {
  switch (owner.forumOwnerKind) {
    case "TOP_LEVEL":
      return formatAbsolutePath("discussions", slug, ...rest);
    case "COMPETITION":
      invariant(
        !!owner.parent,
        `parent must be set for competition topic, slug: ${slug}, owner slug: ${owner.slug}`,
      );
      return formatAbsolutePath(
        "challenges",
        owner.parent,
        "tracks",
        owner.slug,
        "discussions",
        ...rest,
      );
    case "EVENT":
      return formatAbsolutePath(
        "challenges",
        owner.slug,
        "discussions",
        ...rest,
      );
    case "BLOG":
      return formatAbsolutePath("blog", ...rest);
    case "DATASET":
      return formatAbsolutePath("datasets", owner.slug, "discussions", ...rest);
    case "WORKSPACE":
      return formatAbsolutePath(owner.slug, "discussions", ...rest);
    default:
      assertNever(owner.forumOwnerKind);
  }
}

export function getTopicPath({ id, title, forum }: TopicShape, ...rest: Path) {
  if (rest.length) {
    return getForumPath(forum, id, ...rest);
  }
  return getForumPath(forum, id, formatSlug(title));
}

export function getBlogArticlePath(
  { slugLower }: BlogArticleShape,
  ...rest: Path
) {
  return formatAbsolutePath("blog", slugLower, ...rest);
}

export const getDatasetVersionPath = (
  slug: string,
  version: string,
  path?: string,
) =>
  path?.length
    ? generatePath("/datasets/:owner/:slug/versions/:version/*", {
        ...splitDatasetSlug(slug),
        version,
        "*": path,
      })
    : generatePath("/datasets/:owner/:slug/versions/:version", {
        ...splitDatasetSlug(slug),
        version,
      });

// owner: parent event slug
// slug: competition slug
// kind: project version kind file
export const getCompetitionCodePath = (
  owner: string,
  slug: string,
  kind: ProjectVersionFileKind | undefined,
): string =>
  kind
    ? generatePath("/challenges/:owner/tracks/:slug/code/:kind", {
        owner,
        slug,
        kind: formatFileKind(kind),
      })
    : generatePath("/challenges/:owner/tracks/:slug/code", {
        owner,
        slug,
      });

export interface GetEntitySubmmissionCodePathVariables {
  entity: UsernameOrID;
  fileKind: ProjectVersionFileKind;
  owner: string;
  slug: string;
  stage?: number | null | undefined;
  version?: Semver | null | undefined;
}

export const formatEntitySubmissionCodePath = ({
  entity,
  owner,
  slug,
  version,
  fileKind,
  stage,
}: GetEntitySubmmissionCodePathVariables) =>
  intoPath(
    generatePath("/:username/submissions/:owner/:slug/:version/code/:kind", {
      username: entity,
      owner,
      slug,
      version: version ?? "latest",
      kind: formatFileKind(fileKind),
    }),
    { stage },
  );

export const parseFileKind = (
  kind: string | undefined,
): ProjectVersionFileKind => {
  const ret = safeParse(ProjectVersionFileKind, kind);
  return ret.success ? ret.output : "TEMPLATE";
};

export const formatFileKind = (kind: ProjectVersionFileKind | undefined) =>
  kind ? kind.toLowerCase() : "";

const ProjectVersionFileKinds: Record<ProjectVersionFileKind, void> = {
  DATA: undefined,
  PACKAGE: undefined,
  SUBMISSION_EVALUATION: undefined,
  TEMPLATE: undefined,
};

const ProjectVersionFileKind = optional(
  pipe(
    string(),
    toUpperCase(),
    guard(
      (input): input is ProjectVersionFileKind =>
        input in ProjectVersionFileKinds,
    ),
  ),
  "TEMPLATE",
);

export function splitWorkspaceSlug(slug: string) {
  const [owner, localSlug] = slug.split("/", 2);
  return { owner, slug: localSlug };
}

export const splitDatasetSlug = splitWorkspaceSlug;

const OG_IMAGE_ROUTES: Record<OpenGraphParams["type"], string> = {
  event: "/og-image/event/:slug",
  competition: "/og-image/competition/:owner/:slug",
  workspace: "/og-image/workspace/:owner/:slug",
  dataset: "/og-image/dataset/:owner/:slug",
  topic: "/og-image/topic/:id",
  profile: "/og-image/entity/:username",
};

export function getOgImageUrl(params: OpenGraphParams, origin: string): string {
  const { type, ...rest } = params;
  const path = generatePath(OG_IMAGE_ROUTES[type], rest);
  return new URL(path, origin).toString();
}
